import { ref } from 'vue';
import ContactAPI from 'dashboard/api/contacts';
// toISOString() devolve o dia em UTC: depois das 21h (Brasília) virava o dia seguinte
import { toLocalDateString } from '../helpers/dueDate';

function normalize(s) {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

const MONTHS_PT = {
  jan: 0,
  janeiro: 0,
  fev: 1,
  fevereiro: 1,
  mar: 2,
  marco: 2,
  março: 2,
  abr: 3,
  abril: 3,
  mai: 4,
  maio: 4,
  jun: 5,
  junho: 5,
  jul: 6,
  julho: 6,
  julio: 6,
  ago: 7,
  agosto: 7,
  set: 8,
  setembro: 8,
  out: 9,
  outubro: 9,
  nov: 10,
  novembro: 10,
  dez: 11,
  dezembro: 11,
};

const WEEKDAYS_PT = {
  segunda: 1,
  'segunda-feira': 1,
  terca: 2,
  terça: 2,
  'terca-feira': 2,
  'terça-feira': 2,
  quarta: 3,
  'quarta-feira': 3,
  quinta: 4,
  'quinta-feira': 4,
  sexta: 5,
  'sexta-feira': 5,
  sabado: 6,
  sábado: 6,
  domingo: 0,
};

export const TASK_TYPES = [
  { key: 'general', label: 'Geral', icon: 'i-lucide-circle-dot' },
  { key: 'call', label: 'Ligação', icon: 'i-lucide-phone' },
  { key: 'email', label: 'E-mail', icon: 'i-lucide-mail' },
  { key: 'meeting', label: 'Reunião', icon: 'i-lucide-calendar' },
  { key: 'follow_up', label: 'Follow-up', icon: 'i-lucide-refresh-cw' },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'i-lucide-message-circle' },
  { key: 'contract', label: 'Contrato', icon: 'i-lucide-file-signature' },
  { key: 'payment', label: 'Pagamento', icon: 'i-lucide-banknote' },
];

export const PRIORITY_OPTIONS = [
  { key: 'urgent', label: 'Urgente', digit: '1', flag: 'text-n-ruby-9' },
  { key: 'high', label: 'Prioridade', digit: '2', flag: 'text-n-amber-9' },
  { key: 'normal', label: 'Normal', digit: '3', flag: 'text-n-blue-9' },
  { key: 'no_priority', label: 'Baixa', digit: '4', flag: 'text-n-slate-8' },
];

// ── Recorrência ──────────────────────────────────────────────────────────────

const RECURRENCE_PATTERNS = [
  {
    re: /^(todos?\s+os?\s+dias?|diariamente|recorr[eê]ncia\s+di[aá]ria|todo\s+dia)$/i,
    result: () => ({ recurrence_type: 'daily' }),
  },
  {
    re: /^(segunda\s+a\s+sexta|de\s+segunda\s+a\s+sexta|dias?\s+[uú]teis?|segunda-feira\s+a\s+sexta-feira)$/i,
    result: () => ({ recurrence_type: 'weekdays' }),
  },
  {
    re: /^(semanalmente|toda\s+semana|todas?\s+as?\s+semanas?|recorr[eê]ncia\s+semanal)$/i,
    result: () => ({ recurrence_type: 'weekly' }),
  },
  {
    re: /^tod[ao]\s+(seg(?:unda(?:-feira)?)?|ter(?:[cç]a(?:-feira)?)?|qua(?:rta(?:-feira)?)?|qui(?:nta(?:-feira)?)?|sex(?:ta(?:-feira)?)?|s[aá]b(?:ado)?|dom(?:ingo)?)$/i,
    result: m => {
      const raw = normalize(m[1]);
      const map = {
        seg: 1,
        segunda: 1,
        ter: 2,
        terca: 2,
        qua: 3,
        quarta: 3,
        qui: 4,
        quinta: 4,
        sex: 5,
        sexta: 5,
        sab: 6,
        sabado: 6,
        dom: 0,
        domingo: 0,
      };
      const key = raw.replace(/-.*$/, '');
      const dayNum = map[key] ?? map[raw];
      return { recurrence_type: 'weekly', recurrence_weekday: dayNum };
    },
  },
  {
    re: /^(mensalmente|todo\s+m[eê]s|recorr[eê]ncia\s+mensal)$/i,
    result: () => ({ recurrence_type: 'monthly' }),
  },
  {
    re: /^todo\s+(?:dia\s+)?(primeiro|[uú]ltimo\s+dia(?:\s+do\s+m[eê]s)?|\d{1,2})(?:\s+(?:do\s+)?m[eê]s)?$/i,
    result: m => {
      const raw = normalize(m[1]);
      if (raw === 'primeiro')
        return { recurrence_type: 'monthly', recurrence_day: 1 };
      if (raw.startsWith('ultimo'))
        return { recurrence_type: 'monthly', recurrence_day: -1 };
      return { recurrence_type: 'monthly', recurrence_day: parseInt(raw, 10) };
    },
  },
  {
    re: /^dia\s+(\d{1,2})\s+(?:de\s+)?todo\s+m[eê]s$/i,
    result: m => ({
      recurrence_type: 'monthly',
      recurrence_day: parseInt(m[1], 10),
    }),
  },
  {
    re: /^(anualmente|todo\s+ano|uma\s+vez\s+por\s+ano|recorr[eê]ncia\s+anual)$/i,
    result: () => ({
      recurrence_type: 'custom',
      recurrence_interval: 1,
      recurrence_unit: 'years',
    }),
  },
  {
    re: /^(quinzenalmente?|quinzenal)$/i,
    result: () => ({
      recurrence_type: 'custom',
      recurrence_interval: 2,
      recurrence_unit: 'weeks',
    }),
  },
  {
    re: /^a\s+cada\s+(\d+)\s+(dia(?:s)?|semana(?:s)?|m[eê]s(?:es)?|ano(?:s)?)$/i,
    result: m => {
      const interval = parseInt(m[1], 10);
      const unitRaw = normalize(m[2]);
      const unitMap = {
        dia: 'days',
        dias: 'days',
        semana: 'weeks',
        semanas: 'weeks',
        mes: 'months',
        meses: 'months',
        ano: 'years',
        anos: 'years',
      };
      return {
        recurrence_type: 'custom',
        recurrence_interval: interval,
        recurrence_unit: unitMap[unitRaw] ?? 'days',
      };
    },
  },
  {
    re: /^(personalizado|recorr[eê]ncia\s+personalizada)$/i,
    result: () => ({
      recurrence_type: 'custom',
      recurrence_interval: 1,
      recurrence_unit: 'days',
    }),
  },
  {
    re: /^(recorr[eê]ncia\s+manual|manual)$/i,
    result: () => ({ recurrence_type: 'flexible' }),
  },
];

const RECURRENCE_MAX_WORDS = 6;

export function parseRecurrenceToken(raw) {
  const str = raw.trim();
  const hit = RECURRENCE_PATTERNS.map(p => ({
    pattern: p,
    match: str.match(p.re),
  })).find(x => x.match);
  return hit ? hit.pattern.result(hit.match) : null;
}

// Horário exige obrigatoriamente h, : ou . após o número (ex: 14h, 14:00, 14.30)
// ou h seguido de espaço e minutos (ex: 9h 30)
// Número sozinho (ex: "14") não é aceito como horário
const DATE_REGEX =
  /^(hoje|amanh[aã]|(?:pr[oó]xima?\s+)?(?:segunda(?:-feira)?|ter[cç]a(?:-feira)?|quarta(?:-feira)?|quinta(?:-feira)?|sexta(?:-feira)?|s[aá]bado|domingo)|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{1,2}\s+(?:de\s+)?(?:jan(?:eiro)?|fev(?:ereiro)?|mar(?:[cç]o)?|abr(?:il)?|mai(?:o)?|jun(?:ho)?|jul(?:ho|io)?|ago(?:sto)?|set(?:embro)?|out(?:ubro)?|nov(?:embro)?|dez(?:embro)?)\.?)(?:\s+(?:[aà]o?s?\s*)?(\d{1,2})(?:h\s*(\d{0,2})(?:min)?|[:.](\d{2})(?:min)?))?$/i;

// Horário puro: número seguido de h, : ou . (sem parte de data)
const TIME_ONLY_REGEX =
  /^(\d{1,2})(?:h\s*(\d{0,2})(?:min)?|[:](\d{2})(?:min)?)$/i;

function resolveMonth(raw) {
  const n = normalize(raw).replace(/\.$/, '');
  if (MONTHS_PT[n] !== undefined) return MONTHS_PT[n];
  return MONTHS_PT[n.slice(0, 3)];
}

function nextWeekday(target) {
  const today = new Date();
  const current = today.getDay();
  let diff = target - current;
  if (diff <= 0) diff += 7;
  const d = new Date(today);
  d.setDate(today.getDate() + diff);
  return d;
}

export function parseTimeToken(raw) {
  const m = TIME_ONLY_REGEX.exec(raw.trim());
  if (!m) return null;
  const [, hStr, minA, minB] = m;
  const hNum = parseInt(hStr, 10);
  if (hNum < 0 || hNum > 23) return null;
  const minRaw = minA !== undefined ? minA : minB;
  const minNum =
    minRaw !== undefined && minRaw !== '' ? parseInt(minRaw, 10) : 0;
  if (minNum < 0 || minNum > 59) return null;
  const h = String(hNum).padStart(2, '0');
  const min = String(minNum).padStart(2, '0');
  return { due_time: `${h}:${min}` };
}

function clampDate(year, month, day) {
  // Último dia válido do mês: new Date(year, month+1, 0) retorna o último dia
  const lastDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, lastDay));
}

export function parseDateToken(raw) {
  const m = DATE_REGEX.exec(raw.trim());
  if (!m) return null;

  const [, datePart, hStr, minA, minB] = m;
  const minStr = minA !== undefined ? minA : minB;
  const today = new Date();
  let date = null;

  const lower = normalize(datePart.trim());

  if (lower === 'hoje') {
    date = new Date(today);
  } else if (lower === 'amanha') {
    date = new Date(today);
    date.setDate(date.getDate() + 1);
  } else if (/^\d{1,2}\/\d{1,2}/.test(datePart.trim())) {
    const parts = datePart.trim().split('/');
    let year = today.getFullYear();
    if (parts[2]) {
      const y = parseInt(parts[2], 10);
      year = y < 100 ? 2000 + y : y;
    }
    const intendedMonth = parseInt(parts[1], 10) - 1;
    const intendedDay = parseInt(parts[0], 10);
    date = clampDate(year, intendedMonth, intendedDay);
  } else {
    const wdMatch = lower.match(
      /^(?:proxima?\s+)?(segunda|terca|quarta|quinta|sexta|sabado|domingo)/
    );
    if (wdMatch) {
      const wd = WEEKDAYS_PT[wdMatch[1]];
      if (wd === undefined) return null;
      date = nextWeekday(wd);
    } else {
      const parts = datePart.trim().toLowerCase().split(/\s+/);
      const day = parts[0];
      const monRaw = parts[parts.length - 1];
      const monthIdx = resolveMonth(monRaw);
      if (monthIdx === undefined) return null;
      date = clampDate(today.getFullYear(), monthIdx, parseInt(day, 10));
    }
  }

  if (!date || Number.isNaN(date.getTime())) return null;

  const due_date = toLocalDateString(date);
  let due_time = null;
  if (hStr !== undefined) {
    const hNum = parseInt(hStr, 10);
    const minNum =
      minStr !== undefined && minStr !== '' ? parseInt(minStr, 10) : 0;
    if (hNum < 0 || hNum > 23 || minNum < 0 || minNum > 59) return null;
    due_time = `${String(hNum).padStart(2, '0')}:${String(minNum).padStart(2, '0')}`;
  }

  return { due_date, due_time };
}

export function detectPendingToken(el) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;

  const range = sel.getRangeAt(0);
  if (!el.contains(range.startContainer)) return null;

  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return null;

  const textBeforeCursor = node.textContent.slice(0, range.startOffset);
  const lastWord = textBeforeCursor.split(/\s/).pop() ?? '';

  // Prefixos especiais: só funcionam na última palavra (sem espaço ao final)
  if (lastWord) {
    if (lastWord.startsWith('!')) {
      return { type: 'priority', query: lastWord.slice(1), raw: lastWord };
    }
    if (lastWord.startsWith('@')) {
      return { type: 'assignee', query: lastWord.slice(1), raw: lastWord };
    }
    if (lastWord.startsWith('#')) {
      return { type: 'label', query: lastWord.slice(1), raw: lastWord };
    }
    if (lastWord.startsWith('$')) {
      return { type: 'contact', query: lastWord.slice(1), raw: lastWord };
    }
    if (lastWord.startsWith('%')) {
      return { type: 'taskType', query: lastWord.slice(1), raw: lastWord };
    }
    // Barra sozinha alterna o modo descrição: fora dele oferece iniciar,
    // dentro dele oferece finalizar. Ao digitar qualquer caractere depois
    // dela o token some e a barra permanece como texto comum
    if (lastWord === '/') {
      const insideBody = node.parentNode?.closest?.('[data-description-body]');
      return {
        type: insideBody ? 'descriptionEnd' : 'description',
        query: '',
        raw: lastWord,
      };
    }

    const timeOnly = parseTimeToken(lastWord);
    if (timeOnly) {
      return {
        type: 'time-only',
        query: lastWord,
        raw: lastWord,
        parsed: timeOnly,
      };
    }
  }

  if (!textBeforeCursor.trim()) return null;

  const words = textBeforeCursor.trim().split(/\s+/);

  // Recorrência tem prioridade sobre data — testa primeiro para evitar que
  // "toda quinta" seja capturado como data ("quinta" bate no DATE_REGEX)
  const recMaxLen = Math.min(RECURRENCE_MAX_WORDS, words.length);
  const recLengths = Array.from({ length: recMaxLen }, (_, i) => recMaxLen - i);
  const recMatch = recLengths.find(len => {
    const candidate = words.slice(-len).join(' ');
    return parseRecurrenceToken(candidate) !== null;
  });
  if (recMatch !== undefined) {
    const candidate = words.slice(-recMatch).join(' ');
    const parsed = parseRecurrenceToken(candidate);
    return { type: 'recurrence', query: candidate, raw: candidate, parsed };
  }

  // Tenta detectar padrão de data nas últimas 4 palavras
  const maxLen = Math.min(4, words.length);
  const lengths = Array.from({ length: maxLen }, (_, i) => maxLen - i);
  const dateMatch = lengths.find(len => {
    const candidate = words.slice(-len).join(' ');
    return parseDateToken(candidate) !== null;
  });
  if (dateMatch !== undefined) {
    const candidate = words.slice(-dateMatch).join(' ');
    const parsed = parseDateToken(candidate);
    return { type: 'date', query: candidate, raw: candidate, parsed };
  }

  return null;
}

// Divide o conteúdo no marcador de descrição: o que vem antes é título,
// o que vem depois é descrição. Sem marcador, tudo é título.
export function extractTitleAndDescription(el) {
  if (!el) return { title: '', description: null };

  const body = el.querySelector('[data-description-body]');
  let title = '';

  Array.from(el.childNodes).forEach(node => {
    if (node === body) return;
    if (node.nodeType === Node.ELEMENT_NODE && node.dataset.chip) return;
    if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') return;
    title += node.textContent;
  });

  return {
    title: title.trim(),
    description: body ? body.textContent.trim() : null,
  };
}

function buildChipEl(type, value, label, originalText) {
  const span = document.createElement('span');
  span.contentEditable = 'false';
  span.dataset.chip = type;
  span.dataset.value = String(value);
  span.dataset.label = label;
  if (originalText) span.dataset.originalText = originalText;
  span.className =
    type === 'description'
      ? 'text-n-blue-11 font-semibold text-sm select-none align-baseline'
      : 'inline-flex items-center px-1.5 py-0.5 rounded bg-n-blue-2 text-n-blue-11 text-sm mx-0.5 select-none align-baseline';
  span.textContent = label;
  return span;
}

export function insertChipAtCursor(el, raw, chipEl) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;

  const range = sel.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return;

  const text = node.textContent;
  const offset = range.startOffset;
  const before = text.slice(0, offset);
  const rawIdx = before.lastIndexOf(raw);
  if (rawIdx === -1) return;

  const beforeText = text.slice(0, rawIdx);
  const afterText = text.slice(offset);

  const parent = node.parentNode;
  const beforeNode = document.createTextNode(beforeText);
  const afterContent = afterText.startsWith(' ') ? afterText : ` ${afterText}`;
  const afterNode = document.createTextNode(afterContent);

  parent.insertBefore(beforeNode, node);
  parent.insertBefore(chipEl, node);
  parent.insertBefore(afterNode, node);
  parent.removeChild(node);

  const newRange = document.createRange();
  newRange.setStart(afterNode, 1);
  newRange.collapse(true);
  sel.removeAllRanges();
  sel.addRange(newRange);
}

// Modo descrição: insere o marcador "/" seguido de um wrapper editável que
// carrega o texto restante. Tudo digitado dentro do wrapper herda o azul.
function insertDescriptionMode(el, raw) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;

  const range = sel.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return null;

  const text = node.textContent;
  const offset = range.startOffset;
  const rawIdx = text.slice(0, offset).lastIndexOf(raw);
  if (rawIdx === -1) return null;

  const marker = buildChipEl('description', 'on', '/', raw);
  const wrapper = document.createElement('span');
  wrapper.dataset.descriptionBody = 'true';
  wrapper.className = 'text-n-blue-11';
  wrapper.textContent = ` ${text.slice(offset)}`;

  const parent = node.parentNode;
  parent.insertBefore(document.createTextNode(text.slice(0, rawIdx)), node);
  parent.insertBefore(marker, node);
  parent.insertBefore(wrapper, node);
  parent.removeChild(node);

  // Move para dentro do wrapper tudo que sobrou depois dele, para que o
  // conteúdo pré-existente também conte como descrição
  while (wrapper.nextSibling) wrapper.appendChild(wrapper.nextSibling);

  const newRange = document.createRange();
  newRange.setStart(wrapper.firstChild, 1);
  newRange.collapse(true);
  sel.removeAllRanges();
  sel.addRange(newRange);
  return true;
}

// Encerra o modo descrição: remove a barra digitada e devolve ao título tudo
// que estava depois dela dentro do corpo da descrição
function endDescriptionMode(el, raw) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;

  const range = sel.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return null;

  const body = node.parentNode?.closest?.('[data-description-body]');
  if (!body) return null;

  const text = node.textContent;
  const offset = range.startOffset;
  const rawIdx = text.slice(0, offset).lastIndexOf(raw);
  if (rawIdx === -1) return null;

  // Extrai do corpo tudo a partir da barra; o texto anterior segue na descrição
  const after = document.createRange();
  after.setStart(node, rawIdx);
  after.setEnd(body, body.childNodes.length);
  const tail = after.extractContents();

  // Descarta a barra do trecho extraído, o resto volta a ser título
  if (tail.firstChild?.nodeType === Node.TEXT_NODE) {
    tail.firstChild.textContent = tail.firstChild.textContent.slice(raw.length);
  }

  // Âncora não-editável logo após o corpo: impede que o cursor volte para
  // dentro do wrapper por afinidade, garantindo que o texto seguinte seja título
  const anchor = document.createElement('span');
  anchor.contentEditable = 'false';
  anchor.dataset.descriptionEnd = 'true';
  anchor.className = 'inline-block w-0';

  const tailNode = document.createTextNode(' ');
  body.parentNode.insertBefore(anchor, body.nextSibling);
  body.parentNode.insertBefore(tailNode, anchor.nextSibling);
  body.parentNode.insertBefore(tail, tailNode.nextSibling);

  // Descrição vazia: some com o corpo e com o marcador que a introduzia
  if (!body.textContent.trim()) {
    const marker = body.previousSibling;
    if (marker?.dataset?.chip === 'description') marker.remove();
    body.remove();
  }

  const newRange = document.createRange();
  newRange.setStart(tailNode, tailNode.textContent.length);
  newRange.collapse(true);
  sel.removeAllRanges();
  sel.addRange(newRange);
  return true;
}

export function confirmToken(el, pendingToken, option, chips) {
  if (!pendingToken) return null;

  if (pendingToken.type === 'descriptionEnd') {
    if (!endDescriptionMode(el, pendingToken.raw)) return null;
    const idx = chips.value.findIndex(c => c.type === 'description');
    if (idx !== -1) chips.value.splice(idx, 1);
    return { type: 'descriptionEnd' };
  }

  if (pendingToken.type === 'description') {
    if (chips.value.some(c => c.type === 'description')) return null;
    if (!insertDescriptionMode(el, pendingToken.raw)) return null;
    const chipData = {
      type: 'description',
      value: 'on',
      label: '/',
      raw: pendingToken.raw,
      originalText: pendingToken.raw,
      fields: {},
    };
    chips.value.push(chipData);
    return chipData;
  }

  let chipData = null;

  if (pendingToken.type === 'date') {
    const parsed = pendingToken.parsed;
    if (!parsed) return null;
    const labelParts = [];
    if (parsed.due_date) {
      const d = new Date(`${parsed.due_date}T12:00:00`);
      labelParts.push(
        d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
      );
    }
    if (parsed.due_time) labelParts.push(parsed.due_time.replace(':', 'h'));
    chipData = {
      type: 'date',
      value: JSON.stringify(parsed),
      label: labelParts.join(' '),
      raw: pendingToken.raw,
      originalText: pendingToken.raw,
      fields: parsed,
    };
  } else if (pendingToken.type === 'time-only') {
    const { due_time } = pendingToken.parsed;
    // Verifica se já existe chip de data para fazer merge
    const existingDate = chips.value.find(c => c.type === 'date');
    let due_date;
    let originalText;
    if (existingDate) {
      due_date = existingDate.fields.due_date;
      // Restaurar o texto original do chip de data anterior + horário atual
      originalText = `${existingDate.originalText ?? existingDate.raw} ${pendingToken.raw}`;
    } else {
      // Infere data: hoje se horário ainda não passou, amanhã se já passou
      const now = new Date();
      const [hh, mm] = due_time.split(':').map(Number);
      const todayCandidate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hh,
        mm
      );
      if (todayCandidate > now) {
        due_date = toLocalDateString(now);
      } else {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        due_date = toLocalDateString(tomorrow);
      }
      originalText = pendingToken.raw;
    }
    const fields = { due_date, due_time };
    const d = new Date(`${due_date}T12:00:00`);
    const labelParts = [
      d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }),
      due_time.replace(':', 'h'),
    ];
    chipData = {
      type: 'date',
      value: JSON.stringify(fields),
      label: labelParts.join(' '),
      raw: pendingToken.raw,
      originalText,
      fields,
    };
  } else if (pendingToken.type === 'priority' && option) {
    chipData = {
      type: 'priority',
      value: option.key,
      label: `!${option.label}`,
      raw: pendingToken.raw,
      originalText: pendingToken.raw,
      fields: { priority: option.key },
    };
  } else if (pendingToken.type === 'assignee' && option) {
    chipData = {
      type: 'assignee',
      value: option.id,
      label: `@${option.name}`,
      raw: pendingToken.raw,
      originalText: pendingToken.raw,
      fields: { assignee_id: option.id },
    };
  } else if (pendingToken.type === 'label' && option) {
    chipData = {
      type: 'label',
      value: option.title,
      label: `#${option.title}`,
      raw: pendingToken.raw,
      originalText: pendingToken.raw,
      fields: { label: option.title },
    };
  } else if (pendingToken.type === 'contact' && option) {
    chipData = {
      type: 'contact',
      value: option.id,
      label: `$${option.name}`,
      raw: pendingToken.raw,
      originalText: pendingToken.raw,
      fields: { contact_id: option.id },
    };
  } else if (pendingToken.type === 'taskType' && option) {
    chipData = {
      type: 'taskType',
      value: option.key,
      label: `%${option.label}`,
      raw: pendingToken.raw,
      originalText: pendingToken.raw,
      fields: { task_type: option.key },
    };
  } else if (pendingToken.type === 'recurrence') {
    const parsed = pendingToken.parsed;
    if (!parsed) return null;
    const labels = {
      daily: 'Todo dia',
      weekdays: 'Seg–Sex',
      weekly: 'Semanal',
      monthly: 'Mensal',
      flexible: 'Rec. manual',
    };
    let label = labels[parsed.recurrence_type] ?? 'Personalizado';
    if (
      parsed.recurrence_type === 'custom' &&
      parsed.recurrence_interval &&
      parsed.recurrence_unit
    ) {
      const unitShort = {
        days: 'd',
        weeks: 'sem',
        months: 'meses',
        years: 'anos',
      };
      const interval = parsed.recurrence_interval;
      const unit = parsed.recurrence_unit;
      if (interval === 2 && unit === 'weeks') {
        label = 'Quinzenal';
      } else if (interval === 1 && unit === 'years') {
        label = 'Anual';
      } else {
        label = `A cada ${interval} ${unitShort[unit] ?? unit}`;
      }
    }
    chipData = {
      type: 'recurrence',
      value: JSON.stringify(parsed),
      label: `↻ ${label}`,
      raw: pendingToken.raw,
      originalText: pendingToken.raw,
      fields: parsed,
    };
  }

  if (!chipData) return null;

  // Tipos únicos: remover chip anterior do mesmo tipo do DOM e do array
  const UNIQUE_TYPES = [
    'priority',
    'assignee',
    'taskType',
    'contact',
    'date',
    'recurrence',
    'description',
  ];
  if (UNIQUE_TYPES.includes(chipData.type)) {
    const existing = chips.value.find(c => c.type === chipData.type);
    if (existing) {
      const oldEl = el.querySelector(
        `[data-chip="${existing.type}"][data-value="${CSS.escape(String(existing.value))}"]`
      );
      if (oldEl) oldEl.remove();
      chips.value = chips.value.filter(c => c !== existing);
    }
  }

  const chipEl = buildChipEl(
    chipData.type,
    chipData.value,
    chipData.label,
    chipData.originalText
  );
  insertChipAtCursor(el, pendingToken.raw, chipEl);
  chips.value.push(chipData);
  return chipData;
}

export function removeLastChip(el, chips) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return false;

  const range = sel.getRangeAt(0);
  // Só age se não há seleção (cursor simples)
  if (!range.collapsed) return false;

  const node = range.startContainer;
  const offset = range.startOffset;

  // Cursor no início do corpo da descrição: desfaz o modo, devolvendo a barra
  // e o texto ao título como conteúdo comum
  const body =
    node.nodeType === Node.TEXT_NODE
      ? node.parentNode?.closest?.('[data-description-body]')
      : node.closest?.('[data-description-body]');
  if (body && offset === 0 && !body.firstChild?.previousSibling) {
    const marker = body.previousSibling;
    if (marker?.dataset?.chip === 'description') {
      const parent = body.parentNode;
      const textNode = document.createTextNode(
        `${marker.dataset.originalText ?? '/'}${body.textContent}`
      );
      parent.insertBefore(textNode, marker);
      parent.removeChild(marker);
      parent.removeChild(body);

      const newRange = document.createRange();
      newRange.setStart(textNode, 1);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);

      const idx = chips.value.findIndex(c => c.type === 'description');
      if (idx !== -1) chips.value.splice(idx, 1);
      return 'restored';
    }
  }

  // Encontra o chip imediatamente antes do cursor
  let prevSibling = null;

  if (node.nodeType === Node.TEXT_NODE) {
    // Cursor no início do text node → chip pode ser o irmão anterior
    if (offset === 0) {
      prevSibling = node.previousSibling;
    } else {
      // Cursor no meio/fim do text node → só restaura se o texto antes do cursor
      // é apenas espaço(s) e o irmão anterior é chip
      const textBefore = node.textContent.slice(0, offset);
      if (/^\s+$/.test(textBefore)) {
        prevSibling = node.previousSibling;
      }
    }
  } else if (node.nodeType === Node.ELEMENT_NODE) {
    prevSibling = node.childNodes[offset - 1] ?? node.previousSibling;
  }

  if (prevSibling?.dataset?.chip) {
    const chipType = prevSibling.dataset.chip;
    const chipValue = prevSibling.dataset.value;

    const existingChip = chips.value.find(
      c => c.type === chipType && String(c.value) === chipValue
    );
    // Usa o originalText gravado no chip (texto exato que o usuário digitou)
    const rawToken =
      prevSibling.dataset.originalText ??
      existingChip?.originalText ??
      existingChip?.raw ??
      prevSibling.dataset.label;

    const parent = prevSibling.parentNode;

    // Se o cursor estava num text node com apenas espaços antes dele,
    // remover esses espaços iniciais para não deixar espaço duplo ao restaurar
    if (
      node.nodeType === Node.TEXT_NODE &&
      offset > 0 &&
      /^\s+$/.test(node.textContent.slice(0, offset))
    ) {
      node.textContent = node.textContent.slice(offset);
    }

    const textNode = document.createTextNode(rawToken);
    parent.insertBefore(textNode, prevSibling);
    parent.removeChild(prevSibling);

    const newRange = document.createRange();
    newRange.setStart(textNode, textNode.textContent.length);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    const idx = chips.value.findIndex(
      c => c.type === chipType && String(c.value) === chipValue
    );
    if (idx !== -1) chips.value.splice(idx, 1);

    // Retorna 'restored' para que o Vue possa suprimir detecção no próximo onInput
    return 'restored';
  }

  return false;
}

export function derivePendingFields(chips, description = null) {
  const fields = {
    description,
    due_date: null,
    due_time: null,
    priority: null,
    assignee_id: null,
    label_list: [],
    task_type: null,
    contact_id: null,
    recurrence_type: null,
    recurrence_interval: null,
    recurrence_unit: null,
    recurrence_day: null,
    recurrence_weekday: null,
  };

  chips.forEach(chip => {
    if (chip.type === 'date') {
      fields.due_date = chip.fields.due_date;
      fields.due_time = chip.fields.due_time;
    } else if (chip.type === 'priority') {
      fields.priority = chip.fields.priority;
    } else if (chip.type === 'assignee') {
      fields.assignee_id = chip.fields.assignee_id;
    } else if (chip.type === 'label') {
      fields.label_list.push(chip.fields.label);
    } else if (chip.type === 'contact') {
      fields.contact_id = chip.fields.contact_id;
    } else if (chip.type === 'taskType') {
      fields.task_type = chip.fields.task_type;
    } else if (chip.type === 'recurrence') {
      fields.recurrence_type = chip.fields.recurrence_type ?? null;
      fields.recurrence_interval = chip.fields.recurrence_interval ?? null;
      fields.recurrence_unit = chip.fields.recurrence_unit ?? null;
      fields.recurrence_day = chip.fields.recurrence_day ?? null;
      fields.recurrence_weekday = chip.fields.recurrence_weekday ?? null;
    }
  });

  return fields;
}

export function useSmartTextParser({ agents, labels }) {
  const contactResults = ref([]);
  const isLoadingContacts = ref(false);
  let contactDebounceTimer = null;
  let contactRequestId = 0;

  function searchContacts(query) {
    if (!query || query.length < 1) {
      contactResults.value = [];
      return;
    }
    clearTimeout(contactDebounceTimer);
    contactDebounceTimer = setTimeout(async () => {
      contactRequestId += 1;
      const thisId = contactRequestId;
      isLoadingContacts.value = true;
      try {
        const { data } = await ContactAPI.search(query, 1, 'name');
        if (thisId !== contactRequestId) return;
        contactResults.value = (data?.payload || []).map(c => ({
          ...c,
          company_name:
            c.company_name ?? c.additional_attributes?.company_name ?? null,
        }));
      } catch {
        contactResults.value = [];
      } finally {
        if (thisId === contactRequestId) isLoadingContacts.value = false;
      }
    }, 200);
  }

  function getDropdownOptions(pendingToken) {
    if (!pendingToken) return [];
    const q = pendingToken.query.toLowerCase();

    if (pendingToken.type === 'priority') {
      return PRIORITY_OPTIONS.filter(
        o => !q || o.label.toLowerCase().includes(q) || o.digit === q
      );
    }
    if (pendingToken.type === 'assignee') {
      return (agents.value ?? []).filter(
        a => !q || a.name.toLowerCase().includes(q)
      );
    }
    if (pendingToken.type === 'label') {
      return (labels.value ?? []).filter(
        l => !q || l.title.toLowerCase().includes(q)
      );
    }
    if (pendingToken.type === 'contact') {
      return contactResults.value;
    }
    if (pendingToken.type === 'taskType') {
      return TASK_TYPES.filter(t => !q || t.label.toLowerCase().includes(q));
    }
    return [];
  }

  return {
    contactResults,
    isLoadingContacts,
    searchContacts,
    getDropdownOptions,
    detectPendingToken,
    confirmToken,
    removeLastChip,
    derivePendingFields,
    extractTitleAndDescription,
    parseDateToken,
    parseRecurrenceToken,
    TASK_TYPES,
    PRIORITY_OPTIONS,
  };
}
